<template>
  <v-container fluid class="border-top" v-resize="scrollToBottom">
    <v-layout row wrap justify-space-between>
      <v-flex xs12>
        <s-textbox label="intervention_description_label" :number="intervention.intervention.description"/>
      </v-flex>
      <v-flex xs12 sm6>
        <div ref="detailsPane">
          <v-layout row wrap justify-space-between>
            <v-flex xs12 sm6>
              <s-textbox label="intervention_author" :text="intervention.intervention.creatorName"/>
            </v-flex>
            <v-flex xs12 sm6>
              <s-textbox label="intervention_category" :text="intervention.intervention.category.description"/>
            </v-flex>
            <v-flex xs12 sm6>
              <s-textbox label="intervention_student_name"
                         :text="intervention.intervention.student.firstName + ' ' + intervention.intervention.student.lastName"/>
            </v-flex>
            <v-flex xs12 sm6>
              <s-textbox label="student_number" :number="getDisplayId"/>
            </v-flex>
            <v-flex xs12 sm6>
              <s-textbox label="intervention_id" :number="intervention.intervention.id"/>
            </v-flex>
            <v-flex xs12 sm6>
              <s-textbox label="intervention_opened" :date="intervention.intervention.creationDate"/>
            </v-flex>
            <template v-if="closed">
              <v-flex xs12 sm6>
                <s-textbox label="intervention_closed" :date="intervention.intervention.closureDate"/>
              </v-flex>
              <v-flex xs12 sm6>
                <s-textbox label="intervention_closure_reason" :text="closureReason"/>
              </v-flex>
              <v-flex xs12>
                <s-textbox :scroll="true" label="intervention_resolution_summary"
                           :text="intervention.intervention.closureReasonSummary"/>
              </v-flex>
            </template>
            <v-flex xs12 sm6>
              <s-textbox label="intervention_communication_approach"
                         :text="intervention.intervention.communicationApproach.description"/>
            </v-flex>
            <v-flex xs12 sm6>
              <s-textbox label="intervention_communication_date_label"
                         :date="intervention.intervention.dateOfCommunication"/>
            </v-flex>
            <v-flex xs12 sm6>
              <s-textbox :scroll="true" label="actions" :text="intervention.intervention.studentAction"/>
            </v-flex>
            <v-flex xs12 sm6>
              <div class="ma-2">
                <span class="body-2">{{$t('intervention_meeting_duration_label')}}</span>
                <div class="prevent-overflow">
                  {{$t('intervention_duration_amount', {minutes: intervention.intervention.meetingDuration}, intervention.intervention.meetingDuration)}}
                </div>
              </div>
            </v-flex>
            <v-flex xs12 sm6>
              <s-textbox label="intervention_date_label" :date="intervention.intervention.actionTargetDate"/>
            </v-flex>
            <v-flex xs12 sm6 v-if="intervention.intervention.referee && intervention.intervention.referee.url">
              <s-textbox label="intervention_referee_label"
                         :link="{url: intervention.intervention.referee.url,
                         urlKey: intervention.intervention.referee.urlKey || `referral.${intervention.intervention.referee.description}`}"/>
            </v-flex>
            <v-flex xs12 sm6 class="ma-2 overflow-hidden">
              <v-flex>
                <span class="body-2">{{$t('intervention_reminder_text')}}</span>
              </v-flex>
              <v-flex>
                <span v-if="intervention.intervention.reminderEmailFrequency===0">{{$t('email_no_reminder')}}</span>
                <span v-else>{{$t('days', {day: intervention.intervention.reminderEmailFrequency}, intervention.intervention.reminderEmailFrequency)}}</span>
              </v-flex>
            </v-flex>
          </v-layout>
          <v-layout row wrap pr-4 v-if="createdByOrAdminAndNotClose">
            <v-flex xs6 sm6 lg4 pa-1>
              <v-btn block color="error" style="color: white" class="delete_intervention_btn"
                     @click="deleteIntervention(intervention.intervention.id)">
                <v-icon left>delete</v-icon>
                <span>{{$t('delete')}}</span>
              </v-btn>
            </v-flex>
            <v-flex v-if="notClosed" xs6 sm6 lg4 pa-1>
              <v-btn block class="edit_intervention_btn" @click="editIntervention">
                <v-icon left>mode_edit</v-icon>
                <span>{{$t('edit')}}</span>
              </v-btn>
            </v-flex>
            <v-flex v-if="notClosed" xs12 sm12 lg4 pa-1>
              <v-btn block class="close_intervention_btn" @click="closeIntervention" color="success"
                     style="color: white">
                <v-icon left>done_all</v-icon>
                <span>{{$t('intervention_close')}}</span>
              </v-btn>
            </v-flex>
          </v-layout>
        </div>
      </v-flex>
      <v-flex xs12 sm6 pt-2>
        <span class="px-2" style="font-weight: 900;">{{$t('comments')}}</span>
        <div class="comments-section"
             :class="{'grey-background':intervention.intervention.interventionComments.length === 0}"
             ref="comments"
             :style="{height:height}">
          <intervention-comment v-for="(comment, key) in intervention.intervention.interventionComments" :key="key"
                                :comment="comment" :intervention="intervention"/>
          <div v-if="intervention.intervention.interventionComments.length === 0"
               style="position: relative; height: 100%;">
            <v-flex xs12 class="info--text error-message">
              {{$t('intervention_for_student_no_comments')}}
            </v-flex>
          </div>
        </div>
        <v-text-field :hint="$t('intervention_add_comment_hint')" class="pt-2 px-2"
                      :placeholder="$t('intervention_add_comment_placeholder')"
                      :append-icon="isCommentValid ? 'send' : ''" :append-icon-cb="sendComment"
                      @keyup.enter="sendComment" v-model="commentToAdd" :id="commentInteractionId" />
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
  import STextbox from './STextbox'
  import InterventionComment from './InterventionComment'
  import {
    EDIT_INTERVENTION,
    SET_CLOSE_INTERVENTION_FORM_INTERVENTION,
    SET_DRAWER_COMPONENT,
    SET_DRAWER_STATE,
    STUDENT_INTERVENTIONS_OPEN_BY_ID,
    SET_DRAWER_COMPONENT_TITLE
  } from '../../../store/mutation-types'
  import { components, componentTitles } from '../../../store/modules/rightDrawer'
  import {
    DELETE_INTERVENTION_BY_ID,
    POST_INTERVENTION_COMMENT, RESET_INTERVENTION_MODULES
  } from '../../../store/action-types'
  import { mapActions, mapMutations, mapState } from 'vuex'
  import Roles from '../../../mixins/Roles'

  export default {
    components: {
      InterventionComment,
      STextbox
    },
    name: 'student-profile-intervention-body',
    props: ['intervention', 'open', 'reset'],
    mixins: [Roles],
    data () {
      return {
        height: '290px',
        commentToAdd: '',
        timeUp: false
      }
    },
    methods: {
      scrollToBottom () {
        setTimeout(() => {
          // Prevents errors on console when the component has been destroyed.
          if (!this.$refs.comments) {
            return
          }
          // Scroll to the last comment
          this.$refs.comments.scrollTop = this.$refs.comments.scrollHeight
          this.height = `${this.$refs.detailsPane.clientHeight - 95}px`
        }, 10)
      },
      editIntervention () {
        this.$store.dispatch(RESET_INTERVENTION_MODULES)
        this.$store.commit(EDIT_INTERVENTION, this.intervention.intervention)
        this.$store.commit(SET_DRAWER_COMPONENT, components.CREATE_INTERVENTION)
        this.$store.commit(SET_DRAWER_COMPONENT_TITLE, componentTitles.EDIT_INTERVENTION)
        this.$store.commit(SET_DRAWER_STATE, true)
      },
      closeIntervention () {
        this.setDrawerComponent(components.CLOSE_INTERVENTION)
        this.setDrawerComponentTitle(componentTitles.CLOSE_INTERVENTION)
        this.setIntervention(this.intervention.intervention)
        this.setDrawerState(true)
      },
      sendComment () {
        if (this.isCommentValid) {
          const payload = {
            comment: this.commentToAdd,
            id: this.intervention.intervention.id
          }
          this.$store.dispatch(POST_INTERVENTION_COMMENT, payload)
        }
      },
      checkRoute () {
        if (this.intervention.intervention && this.intervention.intervention.id === Number(this.$router.currentRoute.params.intervention_id)) {
          this.$store.commit(STUDENT_INTERVENTIONS_OPEN_BY_ID, this.intervention.id)
        }
      },
      ...mapActions({
        deleteIntervention: DELETE_INTERVENTION_BY_ID
      }),
      ...mapMutations({
        setDrawerComponent: SET_DRAWER_COMPONENT,
        setDrawerComponentTitle: SET_DRAWER_COMPONENT_TITLE,
        setDrawerState: SET_DRAWER_STATE,
        setIntervention: SET_CLOSE_INTERVENTION_FORM_INTERVENTION
      })
    },
    watch: {
      open (value) {
        this.scrollToBottom()
      },
      reset () {
        this.scrollToBottom()
        this.commentToAdd = ''
      }
    },
    computed: {
      createdByOrAdminAndNotClose () {
        const createdBy = this.intervention.intervention.creatorUsername.toLowerCase().trim() === this.username.toLowerCase().trim()
        const notClosed = !this.closed
        return (createdBy || this.isAdmin) && notClosed
      },
      notClosed () {
        return !this.closed
      },
      closed () {
        return this.intervention.intervention.status === 'CLOSED'
      },
      isCommentValid () {
        return this.commentToAdd.trim().length > 0
      },
      ...mapState({
        username: state => state.profile.username,
        closureReasons: state => state.intervention.closureReasons
      }),
      closureReason () {
        let reason = this.closureReasons.find(reason => reason.value === this.intervention.intervention.closureReasonId)
        return reason ? this.$t(reason.text) : this.intervention.intervention.closureReasonId
      },
      commentInteractionId () {
        return 'student-interaction-comment-' + this.intervention.id
      },
      getDisplayId () {
        return this.intervention.intervention.student.displayId ? this.intervention.intervention.student.displayId : this.intervention.intervention.student.studentIdentifier
      }
    },
    mounted () {
      setTimeout(() => {
        this.checkRoute()
        this.scrollToBottom()
      }, 1)
      setTimeout(() => {
        this.timeUp = true
      }, 2000)
    },
    updated () {
      this.scrollToBottom()
    }
  }
</script>

<style scoped>
  .comments-section {
    overflow-y: auto;
  }

  .grey-background {
    background-color: #f2f2f2;
    border-radius: .5em;
    margin-left: .5em;
    margin-right: .5em;
  }

  @media (max-width: 600px) {
    .comments-section {
      overflow-y: auto;
      height: auto !important;
    }
  }

  .border-top {
    border-top: 1px #cdcdcd solid;
  }

  .error-message {
    padding: 1em;
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
</style>



// WEBPACK FOOTER //
// src/components/intervention/studentProfileIntervention/StudentProfileInterventionBody.vue